import React from 'react';
import './BudgetListItem.css';

interface FiscalData {
  value: number;
  fiscal_year: number;
  fiscal_type: string;
}

interface CostItem {
  cost_item_name: string;
  standard_name?: string;
  spending_category?: string;
  description?: string;
  scost_item_supplier?: string;
  fiscal_data: FiscalData[];
}

interface Project {
  pjid: number;
  project_name: string;
  project_type?: string;
  project_description?: string;
  cost_items: CostItem[];
}

interface SearchResult {
  id: string;
  document_id?: number;
  peid?: number;
  line_item?: string;
  line_item_title: string;
  line_item_description?: string;
  organization: string;
  account?: string;
  account_title?: string;
  budget_activity_title?: string;
  program?: string;
  domain?: string;
  supplier?: string;
  projects: Project[];
  total_budget?: number;
  fiscal_years: number[];
  relevance_score?: number;
  highlight?: Record<string, string[]>;
  title: string;
  description: string;
  agency: string;
  budget?: number;
  category: string;
  status: string;
}

interface BudgetListItemProps {
  result: SearchResult;
  onSelect?: (id: string, selected: boolean) => void;
  onClick?: () => void;
  selected?: boolean;
  onBookmark?: (peid: number, title: string) => void;
  isBookmarked?: boolean;
}

const BudgetListItem: React.FC<BudgetListItemProps> = ({ 
  result, 
  onSelect, 
  onClick, 
  selected = false,
  onBookmark,
  isBookmarked = false 
}) => {

  const renderHighlightedText = (text: string, fieldName: string, maxLength: number = 100): React.ReactNode => {
    // Check if we have highlights for this field
    const highlights = result.highlight?.[fieldName];
    
    if (!highlights || highlights.length === 0) {
      // No highlights, just truncate if needed
      const truncated = text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
      return truncated;
    }
    
    // Use the first highlight (which should contain the matched text with <em> tags)
    const highlightedText = highlights[0];
    
    // Parse the highlighted text to create React elements
    const parts = highlightedText.split(/(<em>.*?<\/em>)/g);
    
    const elements = parts.map((part, index) => {
      if (part.startsWith('<em>') && part.endsWith('</em>')) {
        // This is a highlighted part
        const content = part.replace(/<\/?em>/g, '');
        return <mark key={index} className="search-highlight">{content}</mark>;
      }
      return part;
    });
    
    // If the result is too long, truncate it
    const textLength = highlightedText.replace(/<\/?em>/g, '').length;
    if (textLength > maxLength) {
      // For simplicity, just show the truncated version with highlights
      const truncated = text.substring(0, maxLength) + '...';
      return <span>{truncated}</span>;
    }
    
    return <span>{elements}</span>;
  };

  const getProjectCount = () => {
    return result.projects?.length || 0;
  };

  const getCostItemCount = () => {
    return result.projects?.reduce((sum, project) => sum + (project.cost_items?.length || 0), 0) || 0;
  };

  return (
    <tr 
      className={`budget-list-item ${selected ? 'selected' : ''}`}
      onClick={onClick}>
      {/* Selection checkbox */}
      {onSelect && (
        <td className="checkbox-cell">
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => onSelect(result.id, e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            className="item-select"
          />
        </td>
      )}

      {/* Organization */}
      <td className="org-cell">
        <span className="org-badge-small">{result.organization}</span>
      </td>

      {/* Title and Description */}
      <td className="title-cell">
        <div className="title-content">
          <div className="item-title">
            {renderHighlightedText(result.line_item_title, 'line_item_title', 80)}
          </div>
          {result.line_item_description && (
            <div className="item-description">
              {renderHighlightedText(result.line_item_description, 'line_item_description', 120)}
            </div>
          )}
        </div>
      </td>

      {/* Account */}
      <td className="account-cell">
        {result.account_title || 'N/A'}
      </td>

      {/* Program */}
      <td className="program-cell">
        {result.program ? (
          <span className="program-badge">{result.program}</span>
        ) : (
          'N/A'
        )}
      </td>

      {/* Domain */}
      <td className="domain-cell">
        {result.domain ? (
          <span className="domain-badge">{result.domain}</span>
        ) : (
          'N/A'
        )}
      </td>

      {/* Projects/Cost Items */}
      <td className="stats-cell">
        <div className="stats-info">
          <span className="stat-item">{getProjectCount()} projects</span>
          <span className="stat-item">{getCostItemCount()} items</span>
        </div>
      </td>

      {/* Bookmark/Favorite */}
      <td className="bookmark-cell">
        <button
          className={`bookmark-btn ${isBookmarked ? 'bookmarked' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            if (onBookmark && result.peid) {
              onBookmark(result.peid, result.line_item_title);
            }
          }}
          title={isBookmarked ? 'Remove from favorites' : 'Add to favorites'}
        >
          {isBookmarked ? '⭐' : '☆'}
        </button>
      </td>
    </tr>
  );
};

export default BudgetListItem;